import React, { useState, useEffect, useRef, useCallback } from "react";
import PhilosophyNode from "./PhilosophyNode";
import "./PhilosophySection.css";

const philosophyPoints = [
    {
        title: "Purity First",
        description:
            "Our promise is simple: Only products we’d use ourselves. Satvik, Organic authentic essentials for everyday life and ritual.",
    },
    {
        title: "Honest Labels",
        description:
            "What you see is what you get. Every item lists ingredients, origin, and certifications wherever possible so you shop with confidence.",
    },
    {
        title: "Tradition, Reimagined",
        description:
            "We bring together timeless wisdom and modern convenience. Connecting you with foods, pooja items and books that support conscious living.",
    },
];

const DESKTOP_SVG_PATH_D =
  "M 255 170 C 340 120, 480 150, 570 220 C 660 290, 660 400, 570 480 C 480 560, 380 620, 310 700";

const MOBILE_SVG_PATH_D = "M 600 50 V 950";

const PhilosophyTimelineSection = () => {
  const [nodeStyles, setNodeStyles] = useState([]);
  const pathRef = useRef(null);
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const calculateNodeStyles = useCallback(() => {
    if (!isMobile && (!pathRef.current || !containerRef.current)) return;

    const newStyles = [];
    const numNodes = philosophyPoints.length;

    if (isMobile) {
      philosophyPoints.forEach((_, index) => {
        newStyles.push({
          position: "relative",
          marginBottom: "30px",
          transform: "none",
          top: "auto",
          left: "auto",
        });
      });
    } else {
      const pathElement = pathRef.current;
      const currentPathD = DESKTOP_SVG_PATH_D;

      pathElement.setAttribute("d", currentPathD);
      const pathLength = pathElement.getTotalLength();

      philosophyPoints.forEach((_, index) => {
        const pointPercentage = (index + 1) / (numNodes + 1);
        const lengthOnPath = pathLength * pointPercentage;

        const currentPoint = pathElement.getPointAtLength(lengthOnPath);

        const prevPoint = pathElement.getPointAtLength(
          Math.max(0, lengthOnPath - 1)
        );

        let angle =
          Math.atan2(
            currentPoint.y - prevPoint.y,
            currentPoint.x - prevPoint.x
          ) *
          (180 / Math.PI);

        const nodeWidth = 550;
        const nodeHeight = 100;
        let offsetX = 0;
        let offsetY = -nodeHeight / 5;
        let transformOrigin = "center center";

        const distanceOut = 80;
        if (index === 0) {
          offsetX =
            -(nodeWidth / 2) - distanceOut * Math.sin((angle * Math.PI) / 180);
          offsetY =
            -(nodeHeight / 2) - distanceOut * Math.cos((angle * Math.PI) / 180);
          if (angle > 0 && angle < 180) offsetY -= 30;
          else offsetY += 30;
        } else if (index === 1) {
          offsetX =
            -(nodeWidth / 2) + distanceOut * Math.sin((angle * Math.PI) / 180);
          offsetY =
            -(nodeHeight / 2) + distanceOut * Math.cos((angle * Math.PI) / 180);
          if (angle > -90 && angle < 90) offsetY += 30;
          else offsetY -= 30;
        } else {
          offsetX =
            -(nodeWidth / 2) - distanceOut * Math.sin((angle * Math.PI) / 180);
          offsetY =
            -(nodeHeight / 2) - distanceOut * Math.cos((angle * Math.PI) / 180);
          if (angle > 0 && angle < 180) offsetY -= 30;
          else offsetY += 30;
        }

        angle = 0;
        newStyles.push({
          top: `${currentPoint.y + offsetY}px`,
          left: `${currentPoint.x + offsetX}px`,
          transform: `rotate(${angle}deg)`,
          transformOrigin: transformOrigin,
        });
      });
    }
    setNodeStyles(newStyles);
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    calculateNodeStyles();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateNodeStyles]);

  return (
    <section
      className="philosophy-timeline-section section-margin-top-common"
      style={{ marginBottom: "0", paddingBottom: "0" }}
    >
      <h2 className="_hTitle_s5E_ workflow-title">Rooted in Values</h2>
      <p
        className="_hSubtitle_t6F_"
        style={{ paddingBottom: "0px", marginBottom: "0px" }}
      >
          At Vanidya, we blend purity, clarity, and cultural roots to create a marketplace you can trust.
      </p>

      <div className="con-rma-wrapper">
        <div className="philosophy-timeline-svg-container" ref={containerRef}>
          <div className="kl-sap">
            {!isMobile && (
              <svg
                className="svg-flow-path"
                viewBox="0 0 1200 1000"
                preserveAspectRatio="xMidYMid meet"
              >
                <path ref={pathRef} d={DESKTOP_SVG_PATH_D}></path>
              </svg>
            )}
          </div>
          {philosophyPoints.map((point, index) =>
            nodeStyles[index] ? (
              <PhilosophyNode
                key={point.title}
                title={point.title}
                description={point.description}
                animationDelay={0.3 + index * 0.2}
                index={index}
                style={nodeStyles[index]}
              />
            ) : null
          )}
        </div>
      </div>
    </section>
  );
};

export default PhilosophyTimelineSection;
